import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Check, X, Calendar, Flame, User, Edit3, Eye, Crown, Lock, Globe, Printer, ShieldAlert } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { containersApi, formatError } from "../lib/api";
import { DIETARY_TAGS, formatDate, freshnessColors, freshnessLabel } from "../lib/format";
import { PostStatusDialog } from "../components/PostStatusDialog";
import { SharingPanel } from "../components/SharingPanel";
import { QFreshQRSticker } from "../components/QFreshQRSticker";
import { LocationPicker, useLocations } from "../components/LocationPicker";
import { useAuth } from "../lib/AuthContext";

const ContainerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [postStatus, setPostStatus] = useState(null); // "consumed" | "discarded" | null
  const [showPersonalWarning, setShowPersonalWarning] = useState(false);
  const { locations, reload: reloadLocations } = useLocations();

  const load = useCallback(async () => {
    try {
      const data = await containersApi.get(id);
      setC(data);
      setForm({
        name: data.name,
        notes: data.notes || "",
        dietary_tags: data.dietary_tags || [],
        calories_per_serving: data.calories_per_serving ?? "",
        servings: data.servings ?? 1,
        storage_date: data.storage_date,
        expiration_date: data.expiration_date,
        owner: data.owner || "You",
        location_id: data.location_id || null,
      });
    } catch (err) {
      console.error("Failed to load container:", err);
      toast.error("Container not found");
      navigate("/");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Personal-storing guard — show a one-time (per-session, per-container)
  // warning when a NON-owner opens a container flagged is_personal so they
  // know their edits will override the owner's private intent.
  useEffect(() => {
    if (!c || !user?.id) return;
    const isNonOwner = c.created_by && c.created_by !== user.id;
    if (!c.is_personal || !isNonOwner) return;
    const ackKey = `qfresh_personal_ack_${c.id}`;
    try {
      if (sessionStorage.getItem(ackKey) === "1") return;
    } catch (e) { /* sessionStorage blocked → still show */ }
    setShowPersonalWarning(true);
  }, [c, user?.id]);

  const acknowledgePersonal = () => {
    try { sessionStorage.setItem(`qfresh_personal_ack_${c.id}`, "1"); } catch (e) {}
    setShowPersonalWarning(false);
  };

  if (!c) return <div className="p-12 text-center text-[#4F5D75]">Loading…</div>;

  const role = c.role || "viewer";
  const canEdit = role === "owner" || role === "editor";
  const canDelete = role === "owner";
  const canManageSharing = role === "owner";
  const colors = freshnessColors(c);

  const markStatus = async (status) => {
    try {
      await containersApi.update(c.id, { status });
      toast.success(`Marked as ${status}`);
      await load();
      if (status === "consumed" || status === "discarded") {
        setPostStatus(status);
      }
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const handleDelete = async () => {
    if (!confirm("Delete this container?")) return;
    try {
      await containersApi.remove(c.id);
      toast.success("Container deleted");
      navigate("/");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const saveEdit = async () => {
    const payload = {
      ...form,
      calories_per_serving: form.calories_per_serving === "" ? null : parseInt(form.calories_per_serving, 10),
      servings: parseInt(form.servings, 10) || 1,
    };
    // Editors can't change `owner` field — strip it client-side too
    if (role === "editor") delete payload.owner;
    try {
      await containersApi.update(c.id, payload);
      toast.success("Updated");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const toggleTag = (t) => {
    setForm((f) => ({
      ...f,
      dietary_tags: f.dietary_tags.includes(t)
        ? f.dietary_tags.filter((x) => x !== t)
        : [...f.dietary_tags, t],
    }));
  };

  return (
    <div className="px-5 pt-6 pb-4">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="text-sm text-[#4F5D75] hover:text-[#2D3142] flex items-center gap-2 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Role banner for non-owners */}
      {role !== "owner" && (
        <div
          className={`mb-4 tup-card p-3 flex items-center gap-3 ${
            role === "editor" ? "border-[#277A82]/40" : "border-[#E2C76F]/40"
          }`}
          data-testid="role-banner"
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
            role === "editor" ? "bg-[#DCEDF0]" : "bg-[#FAF1D7]"
          }`}>
            {role === "editor" ? (
              <Edit3 className="w-4 h-4 text-[#1F5566]" />
            ) : (
              <Eye className="w-4 h-4 text-[#8A6A1A]" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
              {role === "editor" ? "Editor access" : "View only"}
            </div>
            <div className="text-xs text-[#2D3142] truncate flex items-center gap-1" data-testid="role-banner-owner">
              <span>Shared by <span className="font-semibold">{c.owner_name || "the owner"}</span></span>
              {c.is_personal && (
                <Lock
                  className="w-3 h-3 text-[#277A82] shrink-0"
                  data-testid="detail-personal-lock"
                  aria-label="Personal storing"
                />
              )}
              {c.household_name ? <span>· {c.household_name}</span> : null}
            </div>
          </div>
        </div>
      )}

      {/* Primary action: Consumed / Discarded — front-and-centre for active boxes */}
      {canEdit && c.status === "fresh" && !editing && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="tup-card p-5 mb-4 border-2 border-[#277A82]/30"
          data-testid="primary-actions-panel"
        >
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#277A82]">
                Done with this?
              </div>
              <div className="font-heading text-base font-semibold text-[#2D3142]">
                Empty the box
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <Button
              onClick={() => markStatus("consumed")}
              data-testid="primary-consumed-btn"
              className="bg-[#8BC58A] hover:bg-[#6FB66C] text-white h-11"
            >
              <Check className="w-4 h-4 mr-1.5" /> Consumed
            </Button>
            <Button
              onClick={() => markStatus("discarded")}
              data-testid="primary-discarded-btn"
              variant="outline"
              className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#FBEAE5] h-11"
            >
              <X className="w-4 h-4 mr-1.5" /> Discarded
            </Button>
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-4"
      >
        {/* Main */}
        <div className="tup-card p-7">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {c.box_number ? (
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold tracking-wide bg-[#DCEDF0] text-[#1F5566] border border-[#277A82]/30"
                    data-testid="detail-box-label"
                  >
                    Box {c.box_number}
                  </span>
                ) : null}
                <div
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: colors.bg, color: colors.fg }}
                  data-testid="detail-freshness"
                >
                  {freshnessLabel(c)}
                </div>
              </div>
              {editing ? (
                <Input
                  data-testid="edit-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="font-heading text-2xl font-semibold bg-white border-[#E2E0D8] mt-1"
                />
              ) : (
                <h1 className="font-heading text-3xl font-bold text-[#2D3142]" data-testid="detail-name">
                  {c.name}
                </h1>
              )}

              {/* Storage-location pill — directly under the food name. Tappable
                  only when editing AND user is premium; otherwise just a
                  chip showing the current location for context. */}
              <div className="mt-2">
                {editing && canEdit ? (
                  <LocationPicker
                    value={form.location_id}
                    onChange={(lid) => setForm({ ...form, location_id: lid })}
                    locations={locations}
                    onLocationsChange={reloadLocations}
                    isPremium={Boolean(user?.is_premium)}
                    testId="detail-location-picker"
                  />
                ) : (
                  <LocationPicker
                    value={c.location_id}
                    locations={locations}
                    readOnly
                    testId="detail-location-readonly"
                  />
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {!editing ? (
                canEdit && (
                  <Button
                    variant="outline"
                    onClick={() => setEditing(true)}
                    data-testid="edit-btn"
                    className="border-[#E2E0D8]"
                  >
                    <Edit3 className="w-4 h-4 mr-2" /> Edit
                  </Button>
                )
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setEditing(false); load(); }}
                    className="border-[#E2E0D8]"
                  >Cancel</Button>
                  <Button
                    onClick={saveEdit}
                    data-testid="save-edit-btn"
                    className="bg-[#277A82] hover:bg-[#1F5566] text-white"
                  >Save</Button>
                </>
              )}
            </div>
          </div>

          {/* Editable fields or readonly */}
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            <Field label="Stored on" icon={Calendar}>
              {editing ? (
                <Input type="date" value={form.storage_date} onChange={(e) => setForm({ ...form, storage_date: e.target.value })} className="bg-white border-[#E2E0D8]" />
              ) : formatDate(c.storage_date)}
            </Field>
            <Field label="Expires on" icon={Calendar}>
              {editing ? (
                <Input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} className="bg-white border-[#E2E0D8]" />
              ) : formatDate(c.expiration_date)}
            </Field>
            <Field label="Calories / serving" icon={Flame}>
              {editing ? (
                <Input type="number" value={form.calories_per_serving} onChange={(e) => setForm({ ...form, calories_per_serving: e.target.value })} className="bg-white border-[#E2E0D8]" />
              ) : (
                <span>{c.calories_per_serving ? `${c.calories_per_serving} kcal` : "—"}</span>
              )}
            </Field>
            <Field label="Servings" icon={User}>
              {editing ? (
                <Input type="number" min="1" value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} className="bg-white border-[#E2E0D8]" />
              ) : c.servings || 1}
            </Field>
            <Field label="Owner" icon={User}>
              {editing && role === "owner" ? (
                <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="bg-white border-[#E2E0D8]" />
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <span>{c.owner || "You"}</span>
                  {c.is_personal && (
                    <Lock
                      className="w-3.5 h-3.5 text-[#277A82]"
                      data-testid="owner-field-personal-lock"
                      aria-label="Personal storing"
                    />
                  )}
                </span>
              )}
            </Field>
            <Field label="QR code">
              <span className="font-mono text-sm text-[#2D3142]">{c.qr_code}</span>
            </Field>
          </div>

          {/* Tags */}
          <div className="mt-6">
            <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">Dietary tags</Label>
            {editing ? (
              <div className="flex flex-wrap gap-2 mt-3">
                {DIETARY_TAGS.map((tag) => {
                  const active = form.dietary_tags.includes(tag);
                  return (
                    <button
                      type="button"
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                        active ? "bg-[#277A82] text-white border-[#277A82]" : "bg-white text-[#4F5D75] border-[#E2E0D8]"
                      }`}
                    >{tag}</button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-3">
                {(c.dietary_tags || []).length === 0 && <span className="text-sm text-[#8A93A6]">No tags</span>}
                {(c.dietary_tags || []).map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#DCEDF0] text-[#1F5566] border border-[#277A82]/20">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="mt-6">
            <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">Notes</Label>
            {editing ? (
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white border-[#E2E0D8] mt-2" />
            ) : (
              <p className="mt-2 text-[#4F5D75]">{c.notes || "—"}</p>
            )}
          </div>
        </div>

        {/* Sidebar: QR + actions */}
        <div className="space-y-6">
          <div className="tup-card p-6 text-center" data-testid="qr-display">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6] mb-3 flex items-center justify-center gap-2">
              Container QR
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  c.visibility === "private"
                    ? "bg-[#FBEAE5] text-[#9C3E1D]"
                    : "bg-[#DCEDF0] text-[#1F5566]"
                }`}
                data-testid="qr-visibility-pill"
              >
                {c.visibility === "private" ? (
                  <><Lock className="w-3 h-3" /> Private</>
                ) : (
                  <><Globe className="w-3 h-3" /> Shared</>
                )}
              </span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-[#E2E0D8] inline-block">
              <QFreshQRSticker
                code={c.qr_code}
                boxNumber={c.box_number}
                size={200}
                showCaption={false}
              />
            </div>
            <div className="mt-3 font-mono text-sm text-[#2D3142]">{c.qr_code}</div>
            <button
              type="button"
              onClick={() => navigate("/qr-labels")}
              data-testid="goto-qr-labels-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#277A82] hover:bg-[#1F5566] text-white font-semibold text-sm py-2.5 transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print or buy QR labels
            </button>
          </div>

          {canManageSharing && (
            <SharingPanel container={c} onUpdated={setC} />
          )}

          {!canEdit && (
            <div className="tup-card p-5">
              <p className="text-xs text-[#8A93A6] italic" data-testid="status-actions-viewer-note">
                You don't have permission to change this container's status. Ask {c.owner_name || "the owner"} for editor access.
              </p>
            </div>
          )}

          {canDelete && (
            <Button
              onClick={handleDelete}
              data-testid="delete-btn"
              variant="ghost"
              className="w-full text-[#9C3E1D] hover:bg-[#F9E6E0]"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete container
            </Button>
          )}
        </div>
      </motion.div>

      <PostStatusDialog
        open={!!postStatus}
        container={c}
        status={postStatus}
        onClose={() => setPostStatus(null)}
        onUpdated={() => load()}
        onReset={canEdit ? () => markStatus("fresh") : null}
      />

      {/* Personal-storing warning — non-owners see this once per session
          before they can edit a container the creator marked as personal. */}
      <AnimatePresence>
        {showPersonalWarning && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
            data-testid="personal-warning-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 28 }}
              className="w-full max-w-[440px] bg-[#FDFBF7] rounded-3xl p-6 relative"
              data-testid="personal-warning-modal"
            >
              <div className="w-12 h-12 rounded-xl bg-[#DCEDF0] flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-[#277A82]" />
              </div>
              <h2 className="font-heading text-xl font-bold text-[#2D3142] mt-3">
                Personal storage
              </h2>
              <p className="text-sm text-[#4F5D75] mt-2 leading-relaxed">
                This container is marked as <strong>personal storage</strong> by{" "}
                <strong>{c.owner_name || c.owner || "the owner"}</strong>. Only
                they receive expiry alerts for it.
              </p>
              <p className="text-sm text-[#4F5D75] mt-2 leading-relaxed">
                Please be respectful — check in with them before changing
                or discarding anything inside.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  data-testid="personal-warning-back-btn"
                  className="h-11 rounded-2xl bg-white border-2 border-[#E2E0D8] text-[#4F5D75] font-semibold"
                >
                  Go back
                </button>
                <button
                  type="button"
                  onClick={acknowledgePersonal}
                  data-testid="personal-warning-ack-btn"
                  className="h-11 rounded-2xl brand-gradient-bg text-white font-semibold"
                >
                  I understand
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </div>
    <div className="mt-1.5 text-[#2D3142] text-base">{children}</div>
  </div>
);

export default ContainerDetail;
